import { Injectable } from '@angular/core'
import { CombinedGraphQLErrors } from '@apollo/client/errors'

import { Observable, catchError, map, of } from 'rxjs'

import {
  GetAmazonConnectIntegrationSettingGQL,
  GetInboundApiKeysGQL,
  SaveAmazonConnectIntegrationSettingGQL,
  SaveAmazonConnectIntegrationSettingInput,
  IssueInboundApiKeyGQL,
  IssueInboundApiKeyInput,
  RevokeInboundApiKeyGQL,
  RevokeInboundApiKeyInput,
  DisconnectAmazonConnectGQL,
  BlueBeanIntegrationSettingGQL,
  BlueBeanIntegrationSettingQuery,
  CheckSecretKeyExistenceGQL,
  DisconnectAmptalkGQL,
  DisconnectBiztelIntegrationGQL,
  DisconnectBlueBeanIntegrationGQL,
  DisconnectKaikuraGQL,
  DisconnectSalesforceGQL,
  DisconnectZoomGQL,
  GetAutoAssessmentSettingGQL,
  GetBiztelIntegrationSettingGQL,
  GetKaikuraIntegrationSettingGQL,
  GetMaskedKeysGQL,
  GetOutboundIntegrationSettingGQL,
  InboundConnectService,
  OutboundConnectService,
  SaveAmptalkKeyGQL,
  SaveAutoAssessmentSettingGQL,
  SaveBiztelIntegrationGQL,
  SaveBiztelIntegrationInput,
  SaveBlueBeanIntegrationGQL,
  SaveBlueBeanIntegrationInput,
  SaveBlueBeanIntegrationMutation,
  SaveKaikuraKeyGQL,
  SaveOutboundIntegrationSettingGQL,
  SaveSalesforceTokenGQL,
  SaveZoomTokenGQL,
  SecretKeyType,
  ValidateSalesforceIntegrationSettingGQL,
} from '../../../_generated/graphql'

export type BlueBeanIntegrationSettingView = NonNullable<
  BlueBeanIntegrationSettingQuery['blueBeanIntegrationSetting']
>

export type SaveBlueBeanIntegrationResult =
  SaveBlueBeanIntegrationMutation['saveBlueBeanIntegration']

import {
  AmazonConnectIntegrationSetting,
  InboundApiKey,
  mappingAmazonConnectIntegrationSetting,
  mappingInboundApiKeys,
  mappingAutoAssessmentSetting,
  AutoAssessmentSetting,
  mappingOutboundIntegrationSetting,
  OutboundIntegrationSetting,
  mappingKaikuraIntegrationSetting,
  KaikuraIntegrationSetting,
  mappingBiztelIntegrationSetting,
  BiztelIntegrationSetting,
  mappingSalesforceIntegrationSetting,
  SalesforceValidationResult,
} from './external-integration.mapping'
import { throwIfCustomError } from '../../util/throwIfCustomError'
import { assertGraphqlData } from '../../util/assertGraphqlData'

/** Amazon Connect の操作に限定して表示する業務エラー。 */
export class AmazonConnectOperationError extends Error {
  constructor(
    readonly code: 'BAD_REQUEST' | 'NOT_FOUND' | 'FORBIDDEN',
    message: string,
  ) {
    super(message)
  }
}

/** 共通分類が例外を投げる場合も、元の GraphQL エラーから操作固有の分類を行う。 */
function throwAmazonConnectError(error: unknown): never {
  try {
    throwIfCustomError(error)
  } catch (classifiedError) {
    if (CombinedGraphQLErrors.is(error)) {
      const first = error.errors[0]
      const code = first?.extensions?.code
      if (
        code === 'BAD_REQUEST' ||
        code === 'NOT_FOUND' ||
        code === 'FORBIDDEN'
      )
        throw new AmazonConnectOperationError(code, first.message)
    }
    throw classifiedError
  }
  throw error
}

@Injectable({
  providedIn: 'root',
})
export class ExternalIntegrationService {
  constructor(
    private getAmazonConnectIntegrationSettingGQL: GetAmazonConnectIntegrationSettingGQL,
    private getInboundApiKeysGQL: GetInboundApiKeysGQL,
    private saveAmazonConnectIntegrationSettingGQL: SaveAmazonConnectIntegrationSettingGQL,
    private issueInboundApiKeyGQL: IssueInboundApiKeyGQL,
    private revokeInboundApiKeyGQL: RevokeInboundApiKeyGQL,
    private disconnectAmazonConnectGQL: DisconnectAmazonConnectGQL,
    private getMaskedKeysGQL: GetMaskedKeysGQL,
    private getAutoAssessmentSettingGQL: GetAutoAssessmentSettingGQL,
    private getOutboundIntegrationSettingGQL: GetOutboundIntegrationSettingGQL,
    private getKaikuraIntegrationSettingGQL: GetKaikuraIntegrationSettingGQL,
    private saveAmptalkKeyGQL: SaveAmptalkKeyGQL,
    private saveKaikuraKeyGQL: SaveKaikuraKeyGQL,
    private saveZoomTokenGQL: SaveZoomTokenGQL,
    private saveSalesforceTokenGQL: SaveSalesforceTokenGQL,
    private saveAutoAssessmentSettingGQL: SaveAutoAssessmentSettingGQL,
    private saveOutboundIntegrationSettingGQL: SaveOutboundIntegrationSettingGQL,
    private saveBlueBeanIntegrationGQL: SaveBlueBeanIntegrationGQL,
    private disconnectAmptalkGQL: DisconnectAmptalkGQL,
    private disconnectKaikuraGQL: DisconnectKaikuraGQL,
    private disconnectZoomGQL: DisconnectZoomGQL,
    private disconnectBlueBeanIntegrationGQL: DisconnectBlueBeanIntegrationGQL,
    private CheckSecretKeyExistenceGQL: CheckSecretKeyExistenceGQL,
    private validateSalesforceIntegrationSettingGQL: ValidateSalesforceIntegrationSettingGQL,
    private disconnectSalesforceGQL: DisconnectSalesforceGQL,
    private blueBeanIntegrationSettingGQL: BlueBeanIntegrationSettingGQL,
    private getBiztelIntegrationSettingGQL: GetBiztelIntegrationSettingGQL,
    private saveBiztelIntegrationGQL: SaveBiztelIntegrationGQL,
    private disconnectBiztelIntegrationGQL: DisconnectBiztelIntegrationGQL,
  ) {}

  /** Amazon Connect の管理情報を取得する。 */
  getAmazonConnectIntegrationSetting(): Observable<AmazonConnectIntegrationSetting | null> {
    return this.getAmazonConnectIntegrationSettingGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingAmazonConnectIntegrationSetting(
          res.data.getAdminOrg.amazonConnectIntegrationSetting,
        )
      }),
      catchError((error: unknown) => {
        throwAmazonConnectError(error)
      }),
    )
  }

  /** API キーの一覧を取得する。 */
  getInboundApiKeys(): Observable<InboundApiKey[]> {
    return this.getInboundApiKeysGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingInboundApiKeys(res.data.getAdminOrg.inboundApiKeys)
      }),
      catchError((error: unknown) => {
        throwAmazonConnectError(error)
      }),
    )
  }

  /** Amazon Connect の管理操作を実行し、業務エラーを分類する。 */
  saveAmazonConnectIntegrationSetting(
    input: SaveAmazonConnectIntegrationSettingInput,
  ) {
    return this.saveAmazonConnectIntegrationSettingGQL
      .mutate({ variables: { input } })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.saveAmazonConnectIntegrationSetting
        }),
        catchError((error: unknown) => {
          throwAmazonConnectError(error)
        }),
      )
  }

  /** Amazon Connect の管理操作を実行し、業務エラーを分類する。 */
  issueInboundApiKey(input: IssueInboundApiKeyInput) {
    return this.issueInboundApiKeyGQL.mutate({ variables: { input } }).pipe(
      map((res) => {
        assertGraphqlData(res)
        return {
          ...res.data.issueInboundApiKey,
          expiresAt: new Date(res.data.issueInboundApiKey.expiresAt),
        }
      }),
      catchError((error: unknown) => {
        throwAmazonConnectError(error)
      }),
    )
  }

  /** Amazon Connect の管理操作を実行し、業務エラーを分類する。 */
  revokeInboundApiKey(input: RevokeInboundApiKeyInput) {
    return this.revokeInboundApiKeyGQL.mutate({ variables: { input } }).pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.revokeInboundApiKey
      }),
      catchError((error: unknown) => {
        throwAmazonConnectError(error)
      }),
    )
  }

  /** Amazon Connect の管理操作を実行し、業務エラーを分類する。 */
  disconnectAmazonConnect() {
    return this.disconnectAmazonConnectGQL.mutate().pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.disconnectAmazonConnect
      }),
      catchError((error: unknown) => {
        throwAmazonConnectError(error)
      }),
    )
  }

  getAutoAssessmentSetting(
    connectService: InboundConnectService,
  ): Observable<AutoAssessmentSetting | null> {
    return this.getAutoAssessmentSettingGQL
      .fetch({
        variables: { connectService },
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return mappingAutoAssessmentSetting(res.data.getAutoAssessmentSetting)
        }),
      )
  }

  getOutboundIntegrationSetting(
    connectService: OutboundConnectService,
  ): Observable<OutboundIntegrationSetting | null> {
    return this.getOutboundIntegrationSettingGQL
      .fetch({
        variables: { connectService },
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return mappingOutboundIntegrationSetting(
            res.data.getOutboundIntegrationSetting,
          )
        }),
      )
  }

  getKaikuraIntegrationSetting(): Observable<KaikuraIntegrationSetting | null> {
    return this.getKaikuraIntegrationSettingGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingKaikuraIntegrationSetting(
          res.data.getAdminOrg.kaikuraIntegrationSetting,
        )
      }),
    )
  }

  getAmptalkKeys(): Observable<AmptalkKeys> {
    return this.getMaskedKeysGQL
      .fetch({
        variables: { connectService: InboundConnectService.AmpTalk },
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          const maskedKeys = res.data.getMaskedKeys
          const apiKey =
            maskedKeys.find((key) => key.keyType === SecretKeyType.AmptalkApi)
              ?.maskedKey || ''
          const authKey =
            maskedKeys.find((key) => key.keyType === SecretKeyType.AmptalkAuth)
              ?.maskedKey || ''
          return {
            apiKey,
            authKey,
          }
        }),
      )
  }

  checkSecretKeyExistence(secretKeyType: SecretKeyType): Observable<boolean> {
    return this.CheckSecretKeyExistenceGQL.fetch({
      variables: { secretKeyType },
    }).pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.checkSecretKeyExistence
      }),
    )
  }

  validateSalesforceIntegrationSetting(): Observable<SalesforceValidationResult> {
    return this.validateSalesforceIntegrationSettingGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingSalesforceIntegrationSetting(
          res.data.validateSalesforceIntegrationSetting,
        )
      }),
    )
  }

  saveAmptalkKey(apiKey: string, authKey: string) {
    return this.saveAmptalkKeyGQL.mutate({ variables: { apiKey, authKey } })
  }

  saveKaikuraKey(param: SaveKaikuraKeyParam) {
    return this.saveKaikuraKeyGQL
      .mutate({
        variables: {
          serverName: param.serverName ?? null,
          id: param.id ?? null,
          secret: param.secret ?? null,
          skillMapId: param.skillMapId,
          tagAllowlist: param.tagAllowlist,
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          throw error
        }),
      )
  }

  saveZoomToken(code: string) {
    return this.saveZoomTokenGQL.mutate({ variables: { code } })
  }

  saveSalesforceToken(code: string) {
    return this.saveSalesforceTokenGQL.mutate({ variables: { code } })
  }

  saveAutoAssessmentSetting(
    setting: SaveAutoAssessmentSettingParam,
    connectServiceName: InboundConnectService,
  ) {
    return this.saveAutoAssessmentSettingGQL
      .mutate({
        variables: {
          param: {
            ...setting,
            connectServiceName,
          },
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  saveOutboundIntegrationSetting(
    setting: SaveOutboundSettingParam,
    connectServiceName: OutboundConnectService,
  ) {
    return this.saveOutboundIntegrationSettingGQL
      .mutate({
        variables: {
          param: {
            ...setting,
            connectServiceName,
          },
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  disconnectAmptalk() {
    return this.disconnectAmptalkGQL.mutate()
  }

  disconnectKaikura() {
    return this.disconnectKaikuraGQL.mutate()
  }

  disconnectZoom() {
    return this.disconnectZoomGQL.mutate()
  }

  disconnectSalesforce() {
    return this.disconnectSalesforceGQL.mutate()
  }

  saveBlueBeanIntegration(
    input: SaveBlueBeanIntegrationInput,
  ): Observable<SaveBlueBeanIntegrationResult> {
    return this.saveBlueBeanIntegrationGQL
      .mutate({ variables: { input } })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          throw error
        }),
        map((res) => {
          assertGraphqlData(res)
          return res.data.saveBlueBeanIntegration
        }),
      )
  }

  disconnectBlueBeanIntegration() {
    return this.disconnectBlueBeanIntegrationGQL.mutate().pipe(
      catchError((error: unknown) => {
        throwIfCustomError(error)
        throw error
      }),
    )
  }

  getBlueBeanIntegrationSetting(): Observable<BlueBeanIntegrationSettingView | null> {
    return this.blueBeanIntegrationSettingGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.blueBeanIntegrationSetting ?? null
      }),
    )
  }

  getBiztelIntegrationSetting(): Observable<BiztelIntegrationSetting | null> {
    return this.getBiztelIntegrationSettingGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return mappingBiztelIntegrationSetting(
          res.data.getAdminOrg.biztelIntegrationSetting,
        )
      }),
    )
  }

  saveBiztelIntegration(input: SaveBiztelIntegrationInput) {
    return this.saveBiztelIntegrationGQL.mutate({ variables: { input } }).pipe(
      catchError((error: unknown) => {
        throwIfCustomError(error)
        throw error
      }),
    )
  }

  disconnectBiztelIntegration() {
    return this.disconnectBiztelIntegrationGQL.mutate().pipe(
      catchError((error: unknown) => {
        throwIfCustomError(error)
        throw error
      }),
    )
  }
}

export interface TargetUser {
  id: string
  name: string
  email: string
  departmentName?: string | null
  thumbnailPath?: string | null
  reportTo?: {
    id: string
    name: string
  } | null
  license?: {
    id: string
    plan: string
    userMonthlyLimitSeconds: number
    extraChargeLimitSeconds: number
    usedSeconds: number
  } | null
}

export interface AmptalkKeys {
  apiKey: string
  authKey: string
}

export interface SaveKaikuraKeyParam {
  serverName?: string
  id?: string
  secret?: string
  skillMapId: string
  tagAllowlist: string[]
}

export interface SaveAutoAssessmentSettingParam {
  id?: string
  syncStartDate: Date
  skillMapId: string
  phaseIds: string[]
  targetUserIds: string[]
  minTransferDurationSeconds: number | null
  whiteList: string[]
  blackList: string[]
}

export interface SaveOutboundSettingParam {
  id?: string
  targetUserIds: string[]
}
